import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useRejectQRPendingOrder } from "@/hooks/useQRPendingOrders";
import { getPendingOrderId, type QRPendingOrder } from "@/types/qr-orders";

interface QRRejectDialogProps {
  order: QRPendingOrder | null;
  onClose: () => void;
  onResolved?: () => void;
}

export function QRRejectDialog({ order, onClose, onResolved }: QRRejectDialogProps) {
  const { toast } = useToast();
  const reject = useRejectQRPendingOrder();
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (order) setReason("");
  }, [order]);

  const handleSubmit = async () => {
    if (!order) return;
    try {
      await reject.mutateAsync({ id: getPendingOrderId(order), data: { reason: reason.trim() || undefined } });
      toast({ title: "Order rejected" });
      onClose();
      onResolved?.();
    } catch (error) {
      toast({
        title: "Reject failed",
        description: error instanceof Error ? error.message : "Try again",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Reject Order</DialogTitle>
          <DialogDescription>
            Optionally provide a reason for the customer.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-2">
          <Label htmlFor="reject-reason">Reason (optional)</Label>
          <Textarea
            id="reject-reason"
            placeholder="e.g. Item out of stock"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={200}
            rows={3}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={reject.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void handleSubmit()} disabled={reject.isPending}>
            {reject.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Reject Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
